import { produce } from 'immer'
import { isEqual } from 'lodash'
import type OpenAI from 'openai'
import { createJSONStorage, persist } from 'zustand/middleware'
import { createWithEqualityFn } from 'zustand/traditional'

import { AI_CHATBOT_CONFIG } from '../engine/config'
import { Conversation } from '../engine/conversation'
import { initAiDebugLevel } from '../engine/debugStorage'
import { resetRateLimits } from '../engine/pipeline/write/rate-limiter'
import { clearToolCache } from '../engine/runtime/tool-cache'
import type {
  AiChatbotConfig,
  ActiveToolCall,
  ConversationEntry,
  ConversationState,
  PendingToolCall,
} from '../engine/types'
import { generateUuid7 } from '../engine/uuid7'

const STORAGE_KEY = 'neodxp-ai'

// Module-level side-effect owner — not serializable, not reactive
let _conversation: Conversation | null = null
let _activeId: string | undefined = undefined
let _activeUserId: string | undefined = undefined
let _activeConfig: AiChatbotConfig | undefined = undefined
// ID bound at construction time — used to ignore stale callbacks from aborted conversations
let _conversationBoundId: string | undefined = undefined

// ─── Storage ──────────────────────────────────────────────────────────────────

function buildStorage() {
  try {
    localStorage.setItem('__ls_probe__', '')
    localStorage.removeItem('__ls_probe__')
    return {
      getItem: (_: string) => localStorage.getItem(STORAGE_KEY),
      setItem: (_: string, v: string) => localStorage.setItem(STORAGE_KEY, v),
      removeItem: (_: string) => localStorage.removeItem(STORAGE_KEY),
    }
  } catch {
    const mem = new Map<string, string>()
    return {
      getItem: (_: string) => mem.get(STORAGE_KEY) ?? null,
      setItem: (_: string, v: string) => {
        mem.set(STORAGE_KEY, v)
      },
      removeItem: (_: string) => {
        mem.delete(STORAGE_KEY)
      },
    }
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function evictIfNeeded(
  conversations: Record<string, ConversationEntry>,
): Record<string, ConversationEntry> {
  const { maxEntries } = AI_CHATBOT_CONFIG.conversationStore
  const entries = Object.entries(conversations)
  if (entries.length <= maxEntries) return conversations
  entries.sort((a, b) => a[1].updatedAt - b[1].updatedAt)
  return Object.fromEntries(entries.slice(entries.length - maxEntries))
}

function buildPreview(messages: OpenAI.ChatCompletionMessageParam[]): string {
  const first = messages.find(
    (m) => m.role === 'user' && typeof m.content === 'string',
  )
  return typeof first?.content === 'string' ? first.content.slice(0, 60) : ''
}

// ─── Types ────────────────────────────────────────────────────────────────────

// Persisted shape written to localStorage: { [userId]: { [uuid7]: ConversationEntry } }
type PersistedData = Record<string, Record<string, ConversationEntry>>

type ConversationStoreState = {
  messages: OpenAI.ChatCompletionMessageParam[]
  isProcessing: boolean
  isThinking: boolean
  activeToolCalls: ActiveToolCall[]
  pendingToolCalls: PendingToolCall[]
  hasError: boolean
  allConversations: PersistedData
  conversations: Record<string, ConversationEntry>

  setUserId: (userId: string) => void
  init: (id: string, config: AiChatbotConfig) => void
  submit: (text: string) => void
  reset: () => void
  deleteConversation: (id: string) => void
  stop: () => void
  resolveToolCall: (toolCallId: string, result: unknown) => void
  _onConversationState: (state: ConversationState) => void
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useConversationStore =
  createWithEqualityFn<ConversationStoreState>()(
    persist(
      (set, get) => ({
        messages: [],
        isProcessing: false,
        isThinking: false,
        activeToolCalls: [],
        pendingToolCalls: [],
        hasError: false,
        allConversations: {},
        conversations: {},

        setUserId: (userId) => {
          // User change: abort any in-flight conversation and clear in-memory state
          // so the next init() starts clean. Without this, _conversation, _activeId,
          // and _activeConfig keep pointing at the previous user's session until reload.
          if (_activeUserId && _activeUserId !== userId) {
            _conversation?.abort()
            _conversation = null
            _activeId = undefined
            _conversationBoundId = undefined
            _activeConfig = undefined
          }
          _activeUserId = userId
          set({ conversations: get().allConversations[userId] ?? {} })
        },

        init: (id, config) => {
          initAiDebugLevel()
          _activeId = id
          _activeConfig = config
          _conversationBoundId = id

          const existing = get().conversations[id]
          const cachedMessages = existing?.messages ?? []
          const hasCachedAssistant = cachedMessages.some(
            (m) => m.role === 'assistant',
          )

          set({
            messages: cachedMessages,
            isProcessing: false,
            isThinking: false,
            activeToolCalls: [],
            hasError: false,
          })

          const boundId = id
          _conversation = new Conversation(
            (state) => {
              if (_conversationBoundId !== boundId) return
              get()._onConversationState(state)
            },
            cachedMessages,
            { debug: config.debug },
          )

          if (!hasCachedAssistant) {
            const lastUserMsg = [...(config.initialMessages ?? [])]
              .reverse()
              .find((m) => m.role === 'user')
            if (lastUserMsg && typeof lastUserMsg.content === 'string') {
              void _conversation.processUserMessage(config, lastUserMsg.content)
            }
          }
        },

        deleteConversation: (id) => {
          if (!_activeUserId) return
          const userId = _activeUserId
          set(
            produce<ConversationStoreState>((s) => {
              delete s.allConversations[userId]?.[id]
              s.conversations = s.allConversations[userId] ?? {}
            }),
          )
          if (_activeId === id) get().reset()
        },

        submit: (text) => {
          if (!_conversation || !_activeConfig) return
          void _conversation.processUserMessage(_activeConfig, text)
        },

        stop: () => {
          _conversation?.abort()
        },

        resolveToolCall: (toolCallId, result) => {
          _conversation?.resolveToolCall(toolCallId, result)
        },

        reset: () => {
          if (!_activeConfig) return
          clearToolCache()
          resetRateLimits()
          const config = _activeConfig
          const newId = generateUuid7()
          _activeId = newId
          _conversationBoundId = newId
          set({
            messages: [],
            isProcessing: false,
            isThinking: false,
            activeToolCalls: [],
            pendingToolCalls: [],
            hasError: false,
          })
          const boundId = newId
          _conversation = new Conversation(
            (state) => {
              if (_conversationBoundId !== boundId) return
              get()._onConversationState(state)
            },
            [],
            { debug: config.debug },
          )
          const lastUserMsg = [...(config.initialMessages ?? [])]
            .reverse()
            .find((m) => m.role === 'user')
          if (lastUserMsg && typeof lastUserMsg.content === 'string') {
            void _conversation.processUserMessage(config, lastUserMsg.content)
          }
        },

        _onConversationState: (state) => {
          set({
            messages: state.messages,
            isProcessing: state.isProcessing,
            isThinking: state.isThinking,
            activeToolCalls: state.activeToolCalls,
            pendingToolCalls: state.pendingToolCalls,
            hasError: state.hasError ?? false,
          })
          if (
            !state.isProcessing &&
            state.messages.length > 0 &&
            _activeId &&
            _activeUserId
          ) {
            const id = _activeId
            const userId = _activeUserId
            set(
              produce<ConversationStoreState>((s) => {
                if (!s.allConversations[userId]) s.allConversations[userId] = {}
                const existing = s.allConversations[userId]![id]
                s.allConversations[userId] = evictIfNeeded({
                  ...s.allConversations[userId]!,
                  [id]: {
                    messages: state.messages,
                    createdAt: existing?.createdAt ?? Date.now(),
                    updatedAt: Date.now(),
                    preview: existing?.preview || buildPreview(state.messages),
                    error: state.hasError,
                  },
                })
                s.conversations = s.allConversations[userId]!
              }),
            )
          }
        },
      }),
      {
        name: STORAGE_KEY,
        storage: createJSONStorage(() => buildStorage()),
        // Persist only the raw conversation data — no transient UI state or functions
        partialize: (s) => s.allConversations,
        // On rehydrate, slot the raw data back into allConversations
        merge: (persisted, current) => ({
          ...current,
          allConversations: (persisted as PersistedData) ?? {},
        }),
      },
    ),
    isEqual,
  )
